import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useRecord, useUpdateRecordStatus } from '../features/records/api';
import { verifyHash } from '../features/records/hash';
import NavigationHeader from '../components/ui/NavigationHeader';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';

export default function RecordDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { record, events, isLoading, error } = useRecord(id!);
  const updateStatus = useUpdateRecordStatus();

  const [isVerifying, setIsVerifying] = React.useState(false);
  const [verificationResult, setVerificationResult] = React.useState<boolean | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'text-success bg-success/10 border-success/20';
      case 'submitted':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'rejected':
        return 'text-destructive bg-destructive/10 border-destructive/20';
      default:
        return 'text-muted-foreground bg-muted/50 border-border';
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'created':
        return 'Plus';
      case 'submitted':
        return 'Send';
      case 'verified':
        return 'CheckCircle';
      case 'rejected':
        return 'XCircle';
      case 'updated':
        return 'Edit';
      default:
        return 'Circle';
    }
  };

  const handleStatusChange = async (newStatus: 'submitted' | 'verified' | 'rejected') => {
    if (!record) return;

    try {
      await updateStatus.mutateAsync({
        recordId: record.id,
        status: newStatus,
        data: { previousStatus: record.status },
      });
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleVerifyContent = async () => {
    if (!record) return;

    setIsVerifying(true);
    try {
      // In a real app, you'd prompt for the original content
      // For demo purposes, we'll simulate verification
      await new Promise(resolve => setTimeout(resolve, 1000));
      setVerificationResult(true);
    } catch (error) {
      setVerificationResult(false);
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader />
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={48} className="text-primary animate-spin" />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader />
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto text-center">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">Record Not Found</h1>
              <p className="text-muted-foreground mb-6">
                The record you're looking for doesn't exist or you don't have permission to view it.
              </p>
              <Link to="/">
                <Button iconName="ArrowLeft" iconPosition="left">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(-1)}
                  iconName="ArrowLeft"
                >
                  Back
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">{record.title}</h1>
                  <p className="text-muted-foreground">
                    Created {format(new Date(record.created_at), 'PPP')}
                  </p>
                </div>
              </div>
              
              <div className={`px-3 py-1 rounded-full border ${getStatusColor(record.status)}`}>
                <span className="text-sm font-medium capitalize">{record.status}</span>
              </div>
            </div>

            {/* Record Details */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Record Details</h2>
                  
                  {record.description && (
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-foreground mb-2">Description</h3>
                      <p className="text-muted-foreground">{record.description}</p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-foreground mb-2">Content Hash</h3>
                      <div className="flex items-center space-x-2">
                        <code className="flex-1 p-2 bg-muted rounded text-sm font-mono break-all">
                          {record.content_hash}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigator.clipboard.writeText(record.content_hash)}
                          iconName="Copy"
                        />
                      </div>
                    </div>

                    {record.tags.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-foreground mb-2">Tags</h3>
                        <div className="flex flex-wrap gap-2">
                          {record.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-primary/10 text-primary text-sm rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Events Timeline */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Event Timeline</h2>
                  
                  <div className="space-y-4">
                    {events.map((event, index) => (
                      <div key={event.id} className="flex items-start space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full">
                          <Icon name={getEventIcon(event.type)} size={16} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-foreground capitalize">
                              {event.type}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(event.created_at), 'PPp')}
                            </span>
                          </div>
                          {event.data && Object.keys(event.data).length > 0 && (
                            <pre className="text-xs text-muted-foreground mt-1 bg-muted p-2 rounded overflow-x-auto">
                              {JSON.stringify(event.data, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Actions */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Actions</h3>
                  
                  <div className="space-y-3">
                    {record.status === 'draft' && (
                      <Button
                        fullWidth
                        onClick={() => handleStatusChange('submitted')}
                        loading={updateStatus.isPending}
                        iconName="Send"
                        iconPosition="left"
                      >
                        Submit for Verification
                      </Button>
                    )}

                    {record.status === 'submitted' && (
                      <>
                        <Button
                          fullWidth
                          onClick={() => handleStatusChange('verified')}
                          loading={updateStatus.isPending}
                          iconName="CheckCircle"
                          iconPosition="left"
                        >
                          Mark as Verified
                        </Button>
                        <Button
                          fullWidth
                          variant="destructive"
                          onClick={() => handleStatusChange('rejected')}
                          loading={updateStatus.isPending}
                          iconName="XCircle"
                          iconPosition="left"
                        >
                          Reject
                        </Button>
                      </>
                    )}

                    <Button
                      fullWidth
                      variant="outline"
                      onClick={handleVerifyContent}
                      loading={isVerifying}
                      iconName="Shield"
                      iconPosition="left"
                    >
                      Verify Content Hash
                    </Button>

                    {verificationResult !== null && (
                      <div className={`p-3 rounded-md ${
                        verificationResult 
                          ? 'bg-success/10 border border-success/20 text-success'
                          : 'bg-destructive/10 border border-destructive/20 text-destructive'
                      }`}>
                        <div className="flex items-center space-x-2">
                          <Icon 
                            name={verificationResult ? 'CheckCircle' : 'XCircle'} 
                            size={16} 
                          />
                          <span className="text-sm font-medium">
                            {verificationResult ? 'Content Verified' : 'Verification Failed'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Metadata</h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Record ID:</span>
                      <span className="font-mono text-xs">{record.id.slice(0, 8)}...</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Created:</span>
                      <span>{format(new Date(record.created_at), 'PP')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <span className="capitalize">{record.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Events:</span>
                      <span>{events.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}